import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ConfigsService } from 'src/app/core/service/api/configs.service';
import { IntellegenceService } from 'src/app/core/service/api/intellegence.service';
import { ReportResponse, ReportService } from 'src/app/core/service/api/report.service';
import { Globals } from 'src/app/globals';
import { saveAs } from 'file-saver';
import { translate } from '@jsverse/transloco';
import OverPass from '../../../../../core/constants/overpass-groups.json';
import { IqAnalysisSummaryComponent } from '../iq-analysis-summary/iq-analysis-summary.component';

interface StakeholderAnalysis {
  summary: string;
  last_contact: {
    date: string;
    users: string[];
  };
  contact_sentiment: string;
  urgent_issues: string[];
  open_complaints: {
    description: string;
    sentiment: string;
  }[];
  customer_requirement_topics: string[];
}

@Component({
  selector: 'app-iq-summary-modal',
  templateUrl: './iq-summary-modal.component.html',
  styleUrls: ['./iq-summary-modal.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IqSummaryModalComponent {
  @ViewChild('analysisSummary') analysisSummary!: IqAnalysisSummaryComponent;
  @ViewChild('stepper') stepper!: MatStepper;
  showAnalysisSummary: boolean = false;
  selectedStepIndex: number = 0;
  progressbarWidth: string = '33%';
  description: string = '';
  interestedInPolitical: boolean|null = null;
  includeRegulatoryThemeAnalysis: boolean|null = null;
  includeSociologicAnalysis: boolean|null = null;
  authorities: any = [];
  projectTypes: any[] = [];
  projectPhases: any[] = [];
  processes: any[] = [{id: 1, name: 'Analysis', finished: false, inProgress: false, description: translate('iq.analysis_labels.analysis_starting') }, {id: 2, name: 'Context', finished: false, inProgress: false, description: translate('iq.analysis_labels.retrieving_all_relevant_context_data') }, {id: 3, name: 'Stakeholders', finished: false, inProgress: false, description: translate('iq.analysis_labels.combining_context_with_stakeholders') }, {id: 4, name: 'Issues', finished: false, inProgress: false, description: translate('iq.analysis_labels.identifying_potential_issues') }];
  showContent: boolean[] = [true, false, false];
  isProcess: boolean = false;
  stakeholderAnalysis: StakeholderAnalysis | null = null;
  analysisId: number;
  updatedAt: any;
  selectedType: any[] = [];
  selectedPhase: any[] = [];
  responseData: any = [];
  amenitiesTypes: any[] = [
    { id: 1, name: "transportation"},
    { id: 2, name: "healthcare" },
    { id: 3, name: "entertainment"},
    { id: 4, name: "public_service"},
    { id: 5, name: "waste_management"},
    { id: 6, name: "others"},
    { id: 7, name: "retail"},
    { id: 8, name: "boundaries"},
    { id: 9, name: "point_of_interest" },
    { id: 10, name: "asset_owners"}
  ];

  constructor(private dialogRef: DialogRef<IqSummaryModalComponent>, @Inject(MAT_DIALOG_DATA) public data: any, public globals: Globals, private configsService: ConfigsService, private cdRef: ChangeDetectorRef, private intellegenceService: IntellegenceService, private reportService: ReportService){}

  ngOnInit(): void {
    this.isProcess = true;
    if (!this.data.stakeholder?.id) {
      this.configsService.getConfigs({ configs: ["project.project_type","project.project_phase"] }).subscribe((data: any)=>{
        this.projectTypes = data.project_type;
        this.projectPhases = data.project_phase;
        if (this.globals?.projectConfig?.metadata){
          const metadata = this.globals.projectConfig.metadata;
          if (metadata) {
            this.selectedType = metadata.project_type ? metadata.project_type : [];
            this.selectedPhase = metadata.project_phase ? metadata.project_phase : [];
            this.description = this.globals.project.description;
          }
        }
        this.isProcess = false;
        this.cdRef.detectChanges();
      })
    }

    if (this.data.stakeholder?.id) {
      this.getStakeholderSummary();
    }
  }

  close(){
    this.dialogRef.close();
  }

  getStakeholderSummary(redo: boolean = false) {
    this.isProcess = true
    this.intellegenceService.getStakeholderSummary(this.data.stakeholder.id, redo).subscribe({
      next: (res: any) => {
      this.stakeholderAnalysis = res.metadata;
        this.analysisId = res.id;
        this.isProcess = false;
        this.updatedAt = res.updated_at;
        this.cdRef.detectChanges();
      },
      error: (err) => {
        this.isProcess = false;
        this.cdRef.markForCheck();
        this.close();
      }
    })
  }

  onStepChange(event){
    this.progressbarWidth = event.selectedIndex === 0 ? '33%' : event.selectedIndex === 1 ? '55%' : '100%';
    this.showContent = [false, false, false];
    this.showContent[event.selectedIndex] = true;
    if (event.selectedIndex === 2) {
      this.runProcessesSequentially();
    }
  }

  runProcessesSequentially() {
    let index = 0;
    const processNext = () => {
      if (index >= this.processes.length) {
        this.showAnalysisSummary = true;
        this.cdRef.markForCheck();
        return;
      }
      this.processes[index].inProgress = true;
      this.processes[index].finished = false;
      this.cdRef.markForCheck();
      switch(index) {
        case 0: // Context step - Political Analysis
          this.intellegenceService.getPoliticalAnalysis(this.interestedInPolitical)
            .subscribe({
              next: (res) => {
                this.responseData = [...res];
                this.processes[index].inProgress = false;
                this.processes[index].finished = true;
                this.cdRef.markForCheck();
                index++;
                processNext();
              },
              error: (err) => {
                console.error('Political analysis failed:', err);
                this.processes[index].inProgress = false;
                this.cdRef.markForCheck();
                index++;
                processNext();
              }
            });
          break;
          
        case 1: // Stakeholders step - Regulatory Analysis
          this.intellegenceService.getRegulatoryAnalysis(this.includeRegulatoryThemeAnalysis)
            .subscribe({
              next: (res) => {
                this.responseData = [...res, ...this.responseData];
                this.processes[index].inProgress = false;
                this.processes[index].finished = true;
                this.cdRef.markForCheck();
                index++;
                processNext();
              },
              error: (err) => {
                console.error('Regulatory analysis failed:', err);
                this.processes[index].inProgress = false;
                this.cdRef.markForCheck();
                index++;
                processNext();
              }
            });
          break;

        case 2: // Sociology Analysis
          this.intellegenceService.getSociologyAnalysis(this.includeSociologicAnalysis)
            .subscribe({
              next: (res) => {
                this.responseData = [...res, ...this.responseData];
                this.processes[index].inProgress = false;
                this.processes[index].finished = true;
                this.cdRef.markForCheck();
                index++;
                processNext();
              },
              error: (err) => {
                console.error('Sociology analysis failed:', err);
                this.processes[index].inProgress = false;
                this.cdRef.markForCheck();
                index++;
                processNext();
              }
            });
          break;
          
        case 3: // Issues step - Authorities Analysis
          const authoritiesNames = this.authorities.flatMap(authority => {
              const matched = OverPass.filter(item => item.group_type === authority.name);
              if (matched.length > 0) {
                return matched.map(m => ({ element: m.element }));
              } else {
                return [{ element: authority.name }];
              }
            });
          this.intellegenceService.getAuthoritiesAnalysis({authorities: authoritiesNames})
            .subscribe({
              next: (res) => {
                this.responseData = [...res, ...this.responseData];
                this.processes[index].inProgress = false;
                this.processes[index].finished = true;
                this.cdRef.markForCheck();
                index++;
                processNext();
              },
              error: (err) => {
                console.error('Authorities analysis failed:', err);
                this.processes[index].inProgress = false;
                this.cdRef.markForCheck();
                index++;
                processNext();
              }
            });
          break;
      }
    };
    
    processNext();
  }

  stakeholderSummaryReport() {
    this.reportService.stakeholderSummaryReport(this.analysisId)
      .subscribe((report: ReportResponse) => {
        saveAs(report.body, report.filename);
        this.dialogRef.close();
      });
  }

  allItemsDealtWith() {
    if (!this.analysisSummary || !this.analysisSummary.loaded()) {
      return true;
    }
    const { issuesAccordin, stakeholdersAccordin, contextsAccordin } = this.analysisSummary;
    if (!issuesAccordin || !stakeholdersAccordin || !contextsAccordin) {
      return true;
    }
    const issuesDone = issuesAccordin.allItemsDealtWith() || !issuesAccordin.itemsData.length;
    const stakeholdersDone = stakeholdersAccordin.allItemsDealtWith() || !stakeholdersAccordin.itemsData.length;
    const contextsDone = contextsAccordin.allItemsDealtWith() || !contextsAccordin.itemsData.length;
    if (issuesDone && stakeholdersDone && contextsDone) {
      return false;
    }
    return true;
  }

  isFinalStepDisabled(): boolean {
    return this.interestedInPolitical === null || 
          this.includeRegulatoryThemeAnalysis === null || 
          this.includeSociologicAnalysis === null
  }
}

